import { Box, Grid, IconButton, Portal, styled } from "@mui/material";
import { ICardProperty } from "@rf-model/card-property.model";
import PropertyListCardMap from "./PropertyListCardMap";
import PropertyListCard from "./PropertyListCard";
import { useState } from "react";
import { IconList, IconMap } from "@tabler/icons-react";
import PropertyListCardLarge from "./PropertyListCardLarge";
import MxFormL3AutoComplete from "@rf-l3/form/MxForml3AutoComplete";
import { CPropertyListFilterKeys } from "../util/propertyListFilter";
import { A_PROPERTY_ORDER_BY } from "@/src-refactor/modules/shared/model/const/property-order-by.conts";
import MxTypography from "@rf-l1/MxTypography";
import countFormat from "@rf-helper/format/count-format";
import MxTooltipNormal from "@/src-refactor/modules/shared/components/refactor/components/l1/tooltip/MxTooltipNormal";
import MxPagination from "@rf-l1/page/list/MxPaginations";

const MAP_ELEMENT_ID = "property-list-map";

interface PropertyListProps {
  data: ICardProperty[];
  total: number;
  mapResetKey: number;
  pagination?: {
    page: number;
    totalPages: number;
    onChangePage: (page: number) => void;
  };
}

const PropertyList = (props: PropertyListProps) => {
  const [type, setType] = useState<"map" | "list">("list");
  const totalProperties = props.total ?? 0;

  return (
    <>
      <ToggleMap onClick={setType} type={type} />
      {type == "list" && (
        <>
          <ListModeToolbar total={totalProperties} />
          <ListViewWrapper>
            <PropertyListCard properties={props.data} />
            {props.pagination && (
              <PaginationContainer>
                <MxPagination
                  page={props.pagination.page}
                  totalPages={props.pagination.totalPages}
                  onChangePage={props.pagination.onChangePage}
                />
              </PaginationContainer>
            )}
          </ListViewWrapper>
        </>
      )}
      {type == "map" && (
        <ContentList container columnSpacing={2}>
          <Grid container item xs={12} md={5} alignSelf={"flex-start"}>
            <Grid
              container
              justifyContent={"space-between"}
              alignItems="center"
              px={2}
              py={0.5}
            >
              <Grid item xs="auto">
                <MxTypography
                  variant="caption"
                  color="black"
                  lines={1}
                  id="total-properties"
                  data-testid="total-properties"
                >
                  {countFormat(totalProperties) || 0} propiedades encontradas
                </MxTypography>
              </Grid>

              <Grid item xs="auto">
                <MxFormL3AutoComplete
                  withreacthookform={1}
                  name={CPropertyListFilterKeys.orderBy}
                  // label="Ordenar por"
                  items={A_PROPERTY_ORDER_BY}
                  hiddenSearch
                  hiddenSelections
                  background="white"
                />
              </Grid>
            </Grid>
            <List item xs={12}>
              <ListViewWrapper>
                <PropertyListCardLarge properties={props.data} />
                {props.pagination && (
                  <PaginationContainer>
                    <MxPagination
                      page={props.pagination.page}
                      totalPages={props.pagination.totalPages}
                      onChangePage={props.pagination.onChangePage}
                    />
                  </PaginationContainer>
                )}
              </ListViewWrapper>
            </List>
          </Grid>

          <MapContainer item xs={12} md={7}>
            <PropertyListCardMap
              mapId={MAP_ELEMENT_ID}
              properties={props.data}
              resetKey={props.mapResetKey}
            />
          </MapContainer>
        </ContentList>
      )}
    </>
  );
};

export default PropertyList;

const ListModeToolbar = ({ total }: { total: number }) => {
  if (typeof window === "undefined") return null;
  const container = document.getElementById("list-mode-toolbar");
  if (!container) return null;

  return (
    <Portal container={() => container}>
      <Box display="flex" flexWrap="wrap" alignItems="center" gap={1}>
        <MxTypography
          variant="caption"
          color="black"
          id="total-properties"
          data-testid="total-properties"
        >
          {countFormat(total) || 0} propiedades
        </MxTypography>
        <Box minWidth={170}>
          <MxFormL3AutoComplete
            withreacthookform={1}
            name={CPropertyListFilterKeys.orderBy}
            items={A_PROPERTY_ORDER_BY}
            hiddenSearch
            hiddenSelections
            background="white"
          />
        </Box>
      </Box>
    </Portal>
  );
};

const ListViewWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  height: "100%"
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  marginTop: "auto",
  padding: theme.spacing(0, 2, 2)
}));

const List = styled(Grid)(({ theme }) => ({
  paddingRight: 5,
  overflowY: "auto",
  height: "calc(var(--pl-list-height) + 3rem)",
  maxHeight: "calc(var(--pl-list-height) + 3rem)",
  overscrollBehavior: "contain"
}));

const ContentList = styled(Grid)(({ theme }) => ({
  // Variables CSS con valores por defecto
  "--pl-offset": "0px",
  "--pl-ratio": "0.80",
  "--pl-map-extra": "0.10",
  "--pl-base-height": "calc(100dvh - var(--pl-offset))",
  "--pl-map-ratio": "min(0.95, calc(var(--pl-ratio) + var(--pl-map-extra)))",
  "--pl-list-height": "calc(var(--pl-base-height) * var(--pl-ratio)) ",
  "--pl-map-height": "calc(var(--pl-base-height) * var(--pl-map-ratio))",
  // backgroundColor: "green",

  // Reglas responsivas por altura de viewport

  ["@media (min-height: 500px)"]: {
    "--pl-ratio": "0.59"
    // backgroundColor: "red"
  },
  ["@media (min-height: 600px)"]: {
    "--pl-ratio": "0.60"
    // backgroundColor: "red"
  },
  ["@media (min-height: 650px)"]: {
    "--pl-ratio": "0.63"
    // backgroundColor: "red"
  },
  ["@media (min-height: 700px)"]: {
    "--pl-ratio": "0.65"
    // backgroundColor: "red"
  },
  ["@media (min-height: 840px)"]: {
    "--pl-ratio": "0.69"
    // backgroundColor: "orange"
  },

  ["@media (min-height: 904px)"]: {
    "--pl-ratio": "0.70"
    // backgroundColor: "blue"
  },

  ["@media (min-height: 1000px)"]: {
    "--pl-ratio": "0.72"
    // backgroundColor: "yellow"
  },
  ["@media (min-height: 1081px)"]: {
    "--pl-ratio": "0.73"
    // backgroundColor: "yellow"
  }
}));

const MapContainer = styled(Grid)(({ theme }) => ({
  display: "flex",
  height: "var(--pl-map-height)",
  maxHeight: "var(--pl-map-height)",
  overflow: "hidden",

  "& > *": {
    flex: 1,
    height: "100%"
  }
}));

interface ToggleMap {
  onClick: React.Dispatch<React.SetStateAction<"map" | "list">>;
  type: "map" | "list";
}
const ToggleMap = (props: ToggleMap) => {
  return (
    <Portal container={() => document.getElementById("toggle-map")!}>
      <Box display="flex" columnGap={1}>
        <MxTooltipNormal label="Ver mapa" placement="bottom">
          <ToggleButton
            id="toggle-map-button"
            active={props.type == "map"}
            onClick={() => props.onClick("map")}
          >
            <IconMap /> Vista de mapa
          </ToggleButton>
        </MxTooltipNormal>
        <MxTooltipNormal label="Ver lista" placement="bottom">
          <ToggleButton
            id="toggle-list-button"
            active={props.type == "list"}
            onClick={() => props.onClick("list")}
          >
            <IconList /> Vista listado
          </ToggleButton>
        </MxTooltipNormal>
      </Box>
    </Portal>
  );
};

const ToggleButton = styled(IconButton, {
  shouldForwardProp: prop => prop !== "active"
})<{
  active: boolean;
}>(({ theme, active }) => ({
  background: active ? theme.palette.primary.main : "transparent",
  color: active ? "white" : theme.palette.primary.main,
  border: "1px solid #D6EDF9",
  borderRadius: 5,
  padding: 5,
  svg: {
    color: active ? "white" : theme.palette.primary.main
  },
  fontSize: 12,
  fontWeight: 600,
  display: "flex",
  alignItems: "center",
  gap: 5
}));
